import { useEffect, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import Reveal from '../components/Reveal';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import BrandMark from '../components/BrandMark';
import OptimizedImage from '../components/OptimizedImage';
import { useRooms, featureList, addOnList } from '../hooks/useRooms';
import InquiryWidget from '../components/InquiryWidget';
import RoomBookingOptions from './RoomBookingOptions';
import RoomCarousel from './RoomCarousel';
import PropertyInformation from './PropertyInformation';
import './room-page.css';
import './room-media.css';

function PageHeadline({ children }) {
  return <h1>{children}</h1>;
}

function RoomPage({ slug }) {
  const { rooms } = useRooms();
  const [selected, setSelected] = useState(null);
  const [enquire, setEnquire] = useState(false);
  const room = rooms.find((item) => item.slug === slug.toLowerCase());

  useEffect(() => {
    document.title = room ? `${room.name} | Leopard Bliss Yala` : 'Room not found | Leopard Bliss Yala';
    window.scrollTo(0, 0);
  }, [room && room.slug]);

  return (
    <div className="room-page">
      <SiteHeader BrandMark={BrandMark} solid />
      {!room ? (
        <div className="room-body" style={{ paddingTop: 160 }}>
          <div><PageHeadline>We could not find that room.</PageHeadline><a className="text-link" href="/#rooms">See all rooms <SafeIcon name="ArrowRight" /></a></div>
        </div>
      ) : (
        <>
          <section className="room-hero">
            <OptimizedImage className="room-hero__image" src={room.cover_url} alt="" loading="eager" fetchPriority="high" sizes="100vw" width={1920} height={1200} />
            <div>
              <a className="room-hero__back" href="/">← Back to home</a>
              <PageHeadline>{room.name}</PageHeadline>
              <p>{room.tagline}</p>
              {room.updated_at && <p><small>Updated <time dateTime={room.updated_at}>{new Date(room.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</time></small></p>}
            </div>
          </section>
          {room.photos && room.photos.length > 0 && (
            <section className="room-photos">
              <Reveal><h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 'clamp(2rem, 4vw, 2.8rem)', fontWeight: 500, color: '#263d32', margin: '0 0 24px' }}>What does the room look like?</h2></Reveal>
              <RoomCarousel photos={room.photos} roomName={room.name} onSelect={setSelected} />
            </section>
          )}
          <section className="room-body">
            <Reveal>
              <h2>What makes this room special?</h2>
              <p className="room-body__text">{room.description}</p>
              <ul className="room-features">
                <li><SafeIcon name="Check" />Sleeps up to {room.max_guests ?? 2} guests</li>
                {featureList(room).map((feature) => <li key={feature}><SafeIcon name="Check" />{feature}</li>)}
              </ul>
              {addOnList(room).length > 0 && (
                <ul className="room-features">
                  {addOnList(room).map((addOn) => (
                    <li key={addOn}>
                      <SafeIcon name="Check" />
                      {addOn} (Available on request)
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
            <Reveal className="room-book" delay={0.1}>
              <h3>Stay in {room.name}</h3>
              <p>Reserve your dates through our booking partner. Prices shown at checkout.</p>
              <RoomBookingOptions roomName={room.name} onWebsite={() => setEnquire(true)} />
            </Reveal>
          </section>
          <PropertyInformation />
          <div className="room-others">
            <span>Other rooms:</span>
            {rooms.filter((item) => item.slug !== room.slug).map((item) => <a key={item.slug} href={`/${item.name}`}>{item.name}</a>)}
          </div>
        </>
      )}
      <SiteFooter BrandMark={BrandMark} />
      <InquiryWidget roomSlug={room?.slug || ''} openRequest={enquire} onOpenHandled={() => setEnquire(false)} />
      {selected && (
        <div className="lightbox" role="dialog" aria-modal="true" onClick={() => setSelected(null)}>
          <button className="lightbox__close" onClick={() => setSelected(null)} aria-label="Close image"><SafeIcon name="X" /></button>
          <OptimizedImage src={selected.url} alt={selected.caption || room.name} loading="eager" sizes="(max-width: 720px) 88vw, 86vw" width={1600} height={1100} onClick={(event) => event.stopPropagation()} />
          {selected.caption && <p>{selected.caption}</p>}
        </div>
      )}
    </div>
  );
}

export default RoomPage;